import { INSTRUMENT_TYPES, type ActiveInstruments } from "resources/instruments"
import {
  normalizeUrlInstrumentType,
  type UrlInstrument,
  type UrlInstrumentType,
} from "../use-get-instrument-url-params"

// URL override resolution only handles types with branches in buildUrlInstrumentCandidates
// and a matching makeFindAndGetBaseMetadata lookup (e.g. researchTopic normalizes but no-ops).
export interface UrlInstrumentLookupInput {
  type: (typeof INSTRUMENT_TYPES)[UrlInstrumentType]
  id: string
}

export interface UrlInstrumentPreference {
  type: (typeof INSTRUMENT_TYPES)[UrlInstrumentType]
  id: string
}

function etfTickerMicSuffix(ticker: string, mic: string) {
  return `${ticker}.${mic}`
}

function matchesEtfTickerMicSuffix(urlInstrumentId: string, suffix: string) {
  return urlInstrumentId === suffix || urlInstrumentId.endsWith(`.${suffix}`)
}

function findEtfByTickerMicSuffix(
  urlInstrumentId: string,
  etfs: ActiveInstruments["etfs"],
) {
  if (!etfs) {
    return undefined
  }

  let bestMatch: (typeof etfs)[number] | undefined
  let bestSuffixLength = 0

  for (const etf of etfs) {
    const suffix = etfTickerMicSuffix(etf.ticker, etf.mic)
    if (!matchesEtfTickerMicSuffix(urlInstrumentId, suffix)) {
      continue
    }
    if (suffix.length > bestSuffixLength) {
      bestMatch = etf
      bestSuffixLength = suffix.length
    }
  }

  return bestMatch
}

function pushUniqueCandidate(
  candidates: UrlInstrumentLookupInput[],
  candidate: UrlInstrumentLookupInput,
) {
  const isDuplicate = candidates.some(
    (existing) => existing.type === candidate.type && existing.id === candidate.id,
  )
  if (!isDuplicate) {
    candidates.push(candidate)
  }
}

export function buildUrlInstrumentCandidates(
  urlInstrumentType: UrlInstrumentType,
  urlInstrumentId: string,
  activeInstruments: ActiveInstruments,
): UrlInstrumentLookupInput[] {
  const instrumentType = INSTRUMENT_TYPES[urlInstrumentType]
  const candidates: UrlInstrumentLookupInput[] = []

  if (urlInstrumentType === "portfolio") {
    const portfolio = activeInstruments.portfolios?.find(
      (item) => item.alias === urlInstrumentId || item.id === urlInstrumentId,
    )
    if (portfolio) {
      pushUniqueCandidate(candidates, { type: instrumentType, id: portfolio.id })
    }
  }

  if (urlInstrumentType === "etf" && urlInstrumentId.includes(".")) {
    const etfByTickerMic = findEtfByTickerMicSuffix(urlInstrumentId, activeInstruments.etfs)
    if (etfByTickerMic) {
      pushUniqueCandidate(candidates, { type: instrumentType, id: etfByTickerMic.id })
    } else {
      const internalId = urlInstrumentId.split(".")[0]
      const etfByInternalId = activeInstruments.etfs?.find(
        (etf) => etf.internalId === internalId || etf.id === internalId,
      )
      if (etfByInternalId) {
        pushUniqueCandidate(candidates, { type: instrumentType, id: etfByInternalId.id })
      }
    }
  }

  if (urlInstrumentType === "basket") {
    const basket = activeInstruments.baskets?.find(
      (item) => item.ticker === urlInstrumentId
        || item.ticker.toUpperCase() === urlInstrumentId.toUpperCase(),
    )
    if (basket) {
      pushUniqueCandidate(candidates, { type: instrumentType, id: basket.ticker })
    }
  }

  pushUniqueCandidate(candidates, { type: instrumentType, id: urlInstrumentId })
  return candidates
}

export function resolveUrlInstrumentOverride(
  urlInstrument: UrlInstrument | undefined,
  activeInstruments: ActiveInstruments,
  instrumentValidator: (instrument: UrlInstrumentLookupInput) => unknown,
): UrlInstrumentPreference | null {
  const { urlInstrumentId, urlInstrumentType } = urlInstrument ?? {}
  // Guards hand-built UrlInstrument values; useGetUrlInstrumentParams normalizes on the URL path.
  const normalizedUrlInstrumentType = urlInstrumentType
    ? normalizeUrlInstrumentType(urlInstrumentType)
    : null
  if (!urlInstrumentId || !normalizedUrlInstrumentType) {
    return null
  }

  const candidates = buildUrlInstrumentCandidates(
    normalizedUrlInstrumentType,
    urlInstrumentId,
    activeInstruments,
  )
  const match = candidates.find((candidate) => instrumentValidator(candidate))
  return match ? { type: match.type, id: match.id } : null
}
